/**
 * DEV-7091b — AI Assistant API.
 *
 * The chat endpoints live on the WebApp (`/api/ai-assistant/*`, same-origin via
 * the Vite proxy) but the invoke endpoint streams `text/event-stream`, which
 * Axios cannot consume — so streaming uses native `fetch()` + a ReadableStream
 * reader (faithful to the Vue 2 `aiAssistantService.js`). `createSession` is a
 * plain JSON POST. Session list/get/update/delete land with the sidebar in
 * DEV-7281; file upload in DEV-7282.
 */
import { parseSseStream } from '../utils/sse'
import type {
  ApprovalOptions,
  FileUploadResponse,
  InvokeOptions,
  SessionMessagesResponse,
  SessionSummary,
  SseCallbacks,
  StreamHandle,
} from '../types/ai-assistant.types'

const INVOKE_URL = '/api/ai-assistant/invoke'
const SESSIONS_URL = '/api/ai-assistant/sessions'

async function jsonFetch<T>(url: string, init?: RequestInit): Promise<T | null> {
  const options: RequestInit = { cache: 'no-store', ...init }
  options.headers = { ...(init?.headers ?? {}) }
  if (options.body && !('Content-Type' in options.headers)) {
    ;(options.headers as Record<string, string>)['Content-Type'] = 'application/json'
  }
  const resp = await fetch(url, options)
  if (!resp.ok) {
    const txt = await resp.text()
    throw new Error(txt || `HTTP ${resp.status}`)
  }
  if (resp.status === 204) return null
  const ct = resp.headers.get('Content-Type') || ''
  return ct.includes('application/json') ? ((await resp.json()) as T) : ((await resp.text()) as T)
}

/** Build a human-readable error from a non-OK streaming response. */
async function readErrorMessage(response: Response): Promise<string> {
  const errText = await response.text()
  let detail = errText
  try {
    const errJson = JSON.parse(errText)
    detail =
      errJson.message ||
      (typeof errJson.error === 'string' ? errJson.error : null) ||
      errJson.error?.message ||
      JSON.stringify(errJson)
  } catch {
    /* keep raw text */
  }
  return `Error (${response.status}): ${detail}`
}

export class AiAssistantService {
  /** POST a body to /invoke and pipe the SSE stream to `callbacks`. */
  private stream(body: Record<string, unknown>, callbacks: SseCallbacks): StreamHandle {
    const controller = new AbortController()
    let doneEmitted = false

    const emitDone = () => {
      if (!doneEmitted) {
        doneEmitted = true
        callbacks.onDone?.()
      }
    }

    fetch(INVOKE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          callbacks.onError?.(await readErrorMessage(response))
          return
        }
        const reader = response.body?.getReader()
        if (!reader) {
          callbacks.onError?.('Error: response body is not readable')
          return
        }
        await parseSseStream(reader, { ...callbacks, onDone: emitDone })
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') {
          emitDone()
          return
        }
        callbacks.onError?.('Error: ' + (err instanceof Error ? err.message : String(err)))
      })

    return { abort: () => controller.abort() }
  }

  /** Send a prompt and stream the response. */
  invokeAgent(options: InvokeOptions): StreamHandle {
    const body: Record<string, unknown> = { prompt: options.prompt, thread_id: options.threadId }
    if (options.fileNames?.length) body.file_names = options.fileNames
    return this.stream(body, options)
  }

  /** Send an approval decision and stream the continued response. */
  sendApproval(options: ApprovalOptions): StreamHandle {
    return this.stream({ approval: options.decision, thread_id: options.threadId }, options)
  }

  /** Create a chat session (needed to obtain a server-side thread on first send). */
  async createSession(title: string, sessionId: string): Promise<SessionSummary> {
    const result = await jsonFetch<SessionSummary>(SESSIONS_URL, {
      method: 'POST',
      body: JSON.stringify({ title, sessionId }),
    })
    if (!result) throw new Error('createSession returned no body')
    return result
  }

  /** DEV-7281 — list the current user's sessions (most-recent first). */
  async listSessions(): Promise<SessionSummary[]> {
    return (await jsonFetch<SessionSummary[]>(SESSIONS_URL)) ?? []
  }

  /** DEV-7281 — load a session's message history. */
  async getSessionMessages(sessionId: string): Promise<SessionMessagesResponse> {
    const url = `${SESSIONS_URL}/${encodeURIComponent(sessionId)}/messages`
    return (await jsonFetch<SessionMessagesResponse>(url)) ?? { messages: [] }
  }

  /** DEV-7281 — patch a session's title and/or pinned flag. */
  async updateSession(
    sessionId: string,
    changes: { title?: string; pinned?: boolean },
  ): Promise<void> {
    await jsonFetch(`${SESSIONS_URL}/${encodeURIComponent(sessionId)}`, {
      method: 'PATCH',
      body: JSON.stringify(changes),
    })
  }

  /** DEV-7281 — soft-delete a session. */
  async deleteSession(sessionId: string): Promise<void> {
    await jsonFetch(`${SESSIONS_URL}/${encodeURIComponent(sessionId)}`, { method: 'DELETE' })
  }

  /**
   * DEV-7282 — upload files to a session (multipart). `force=true` overwrites
   * duplicates. The backend encrypts the actor/session IDs server-side, so the
   * client just sends the raw files (no client-side crypto).
   */
  async uploadFiles(sessionId: string, files: File[], force = false): Promise<FileUploadResponse> {
    let url = `${SESSIONS_URL}/${encodeURIComponent(sessionId)}/files`
    if (force) url += '?force=true'
    const form = new FormData()
    for (const file of files) form.append('files', file, file.name)

    const resp = await fetch(url, { method: 'POST', body: form })
    if (!resp.ok) {
      const txt = await resp.text()
      throw new Error(txt || `HTTP ${resp.status}`)
    }
    return resp.json()
  }
}
